import { FC } from "react"
import { Badge } from "./ui/badge"
import { Activity, Calendar, Droplets, Globe, Mail, MapPin, Shield, User } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import AccountSummary from "./AccountSummary"
import type { CustomerDTO, WaterConsumptionDTO } from "@/gen/api"
import { getStatusColor } from "@/lib/utils"
import { CreatedAtDisplay } from "./ui/date-display"
import { Switch } from "./ui/switch"
import { AndroidBrandIcon, AppleBrandIcon } from "./PlatformBrandIcons"

const formatDateTime = (date?: string | null) => {
  if (!date) return "never used"
  return new Date(date).toLocaleString()
}

const CustomerInformation: FC<{
  customer: CustomerDTO
  waterConsumption?: WaterConsumptionDTO | undefined
  canWriteCustomer?: boolean
  canMutateCustomer?: boolean
  isUpdatingStatus?: boolean
  onSuspendedChange?: (checked: boolean) => void
  isDeletingCustomer?: boolean
  onDeleteCustomer?: () => void
}> = ({
  customer,
  waterConsumption,
  canWriteCustomer = false,
  canMutateCustomer = false,
  isUpdatingStatus = false,
  onSuspendedChange,
  isDeletingCustomer = false,
  onDeleteCustomer,
}) => {
  const isSuspended = customer.status?.toLowerCase() === "suspended"

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Customer Profile */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="size-5" />
            Customer Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <User className="size-4" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium">{customer.name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <Mail className="size-4" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email Address</p>
                  <p className="font-medium">{customer.email}</p>
                </div>
              </div>

              {/* <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Phone className="size-4" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Phone Number</p>
                <p className="font-medium">{customer.phone}</p>
              </div>
            </div> */}
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <MapPin className="size-4" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{customer.address}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <Calendar className="size-4" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Join Date</p>
                  <p className="font-medium">
                    <CreatedAtDisplay date={customer.joinDate ?? null} />
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <Activity className="size-4" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="flex items-center gap-2">
                    <div
                      className={`size-2 rounded-full ${getStatusColor(customer.status ?? "")}`}
                    ></div>
                    <Badge variant="secondary" className="capitalize">
                      {customer.status}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Droplets className="size-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Water Consumption</p>
                  <p className="font-medium">
                    {waterConsumption
                      ? `${waterConsumption.totalLitres?.toLocaleString() ?? 0} ${waterConsumption.unit ?? "L"}`
                      : "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Actions */}
      <AccountSummary
        {...(customer.email ? { customerEmail: customer.email } : {})}
        {...(customer.name ? { customerName: customer.name } : {})}
        isDeletingCustomer={isDeletingCustomer}
        onDeleteCustomer={onDeleteCustomer}
        canWriteCustomer={canWriteCustomer}
        canMutateCustomer={canMutateCustomer}
      />

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="size-5" />
            Last Login By Platform
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="flex items-center gap-3 rounded-lg border p-4">
            <Globe
              className={`size-5 ${customer.lastLogins?.web ? "text-blue-500" : "text-muted-foreground/40"}`}
            />
            <div>
              <p className="text-sm font-medium">Web</p>
              <p className="text-xs text-muted-foreground">
                {formatDateTime(customer.lastLogins?.web)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border p-4">
            <AppleBrandIcon
              className={`size-5 ${customer.lastLogins?.ios ? "text-blue-500" : "text-muted-foreground/40"}`}
            />
            <div>
              <p className="text-sm font-medium">iOS</p>
              <p className="text-xs text-muted-foreground">
                {formatDateTime(customer.lastLogins?.ios)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border p-4">
            <AndroidBrandIcon
              className={`size-5 ${customer.lastLogins?.android ? "text-blue-500" : "text-muted-foreground/40"}`}
            />
            <div>
              <p className="text-sm font-medium">Android</p>
              <p className="text-xs text-muted-foreground">
                {formatDateTime(customer.lastLogins?.android)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="size-5" />
            Account Control
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div>
              <p className="font-medium">Suspended account</p>
              <p className="text-sm text-muted-foreground">
                Prevents customer login until turned off.
              </p>
            </div>
            <Switch
              checked={isSuspended}
              disabled={!canMutateCustomer || isUpdatingStatus || !onSuspendedChange}
              onCheckedChange={onSuspendedChange}
              aria-label="Suspended account"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default CustomerInformation
